import { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';

function resetTranslate() {
  const root = document.getElementById('root');
  if (root && root.style.transform && root.style.transform !== 'translateY(0px)') {
    root.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
    root.style.transform = 'translateY(0)';
    // Remove transform completely after animation
    setTimeout(() => {
      if (root.style.transform === 'translateY(0)' || root.style.transform === 'translateY(0px)') {
        root.style.transform = '';
        root.style.transition = '';
      }
    }, 250);
  }
}

export function useKeyboard() {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const isNative = Capacitor.isNativePlatform();
  const isIOS = isNative && Capacitor.getPlatform() === 'ios';

  useEffect(() => {
    if (!isIOS) return;

    let keyboardDidShow, keyboardDidHide, keyboardWillHide;
    let currentTranslate = 0;
    let lastKbHeight = 0;

    const setupListeners = async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');

        keyboardDidShow = await Keyboard.addListener('keyboardDidShow', (info) => {
          lastKbHeight = info.keyboardHeight;
          setIsKeyboardVisible(true);
          setKeyboardHeight(info.keyboardHeight);

          // Ignore accessory bar only (height < 100)
          if (info.keyboardHeight < 100) return;

          const input = document.activeElement;
          if (!input || !isInputElement(input)) return;

          const inputRect = input.getBoundingClientRect();
          const visibleArea = window.innerHeight - info.keyboardHeight;

          // If input is already visible above keyboard with buffer, do nothing
          if (inputRect.bottom < visibleArea - 30) return;

          // Calculate shift needed (input bottom should be 60px above keyboard)
          const shiftNeeded = inputRect.bottom - visibleArea + 60;
          if (shiftNeeded <= 0) return;

          // Cap max shift — never shift more than half screen (keeps header partially visible)
          currentTranslate = Math.min(shiftNeeded, window.innerHeight * 0.4);
          const root = document.getElementById('root');
          if (root) {
            root.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
            root.style.transform = `translateY(-${currentTranslate}px)`;
          }
        });

        // Use BOTH willHide and didHide for safety
        keyboardWillHide = await Keyboard.addListener('keyboardWillHide', () => {
          currentTranslate = 0;
          lastKbHeight = 0;
          setIsKeyboardVisible(false);
          setKeyboardHeight(0);
          resetTranslate();
        });

        keyboardDidHide = await Keyboard.addListener('keyboardDidHide', () => {
          currentTranslate = 0;
          lastKbHeight = 0;
          setIsKeyboardVisible(false);
          setKeyboardHeight(0);
          resetTranslate();
        });

      } catch {
        // Silent fail
      }
    };

    // Safety: when input loses focus, reset translate
    const handleFocusOut = (e) => {
      if (!isInputElement(e.target)) return;
      // Delay to check if focus moved to another input (don't reset in that case)
      setTimeout(() => {
        const active = document.activeElement;
        if (!active || !isInputElement(active)) {
          // No input focused — keyboard is closing, reset
          currentTranslate = 0;
          resetTranslate();
        }
      }, 300);
    };

    // When user taps a different input while keyboard is open
    const handleFocusIn = (e) => {
      if (!isInputElement(e.target)) return;
      if (lastKbHeight < 100) return;

      setTimeout(() => {
        const input = e.target;
        const inputRect = input.getBoundingClientRect();
        const actualBottom = inputRect.bottom + currentTranslate;
        const visibleArea = window.innerHeight - lastKbHeight;

        const rawShift = actualBottom > visibleArea - 30
          ? Math.max(0, actualBottom - visibleArea + 60)
          : 0;
        // Cap max shift
        const newShift = Math.min(rawShift, window.innerHeight * 0.4);

        if (newShift !== currentTranslate) {
          currentTranslate = newShift;
          const root = document.getElementById('root');
          if (root) {
            root.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
            root.style.transform = newShift > 0
              ? `translateY(-${currentTranslate}px)`
              : 'translateY(0)';
          }
        }
      }, 150);
    };

    // Safety: reset on any touch outside input (catches edge cases)
    const handleTouchStart = (e) => {
      if (currentTranslate > 0 && !isInputElement(e.target) && !e.target.closest('input, textarea, select')) {
        setTimeout(() => {
          if (currentTranslate > 0) {
            currentTranslate = 0;
            resetTranslate();
          }
        }, 500);
      }
    };

    document.addEventListener('focusin', handleFocusIn, { passive: true });
    document.addEventListener('focusout', handleFocusOut, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });

    setupListeners();

    return () => {
      keyboardDidShow?.remove();
      keyboardDidHide?.remove();
      keyboardWillHide?.remove();
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('touchstart', handleTouchStart);
      // Always reset on unmount (route change)
      currentTranslate = 0;
      resetTranslate();
    };
  }, [isIOS]);

  const dismissKeyboard = useCallback(async () => {
    if (!isNative) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return;
    }
    try {
      const { Keyboard } = await import('@capacitor/keyboard');
      await Keyboard.hide();
    } catch {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, [isNative]);

  return { isKeyboardVisible, keyboardHeight, dismissKeyboard, isNative };
}

function isInputElement(element) {
  const tagName = element.tagName.toLowerCase();
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
}

export default useKeyboard;
